import { FunctionComponent, useState } from "react";
import {
  Controller,
  useController,
  useFormContext,
  useFormState,
} from "react-hook-form";
import { getError, getTranslationError } from "../utils";
import { useLocale } from "stores/hooks/locale";
import TranslationSelector from "../translation/selector";
import { StyledInputContainer, StyledTextarea } from "./textarea.styles";
import Label from "../label";
import Warning from "../warning";
import Field from "../field";

interface TextInputProps {
  name: string;
  label: string;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  password?: boolean;
  autoComplete?: "username" | "current-password" | "new-password";
  translations?: boolean;
  height?: number;
}

const TextareaInput: FunctionComponent<TextInputProps> = ({
  name,
  label,
  required = false,
  disabled = false,
  maxLength = 32768,
  autoComplete,
  translations = false,
  height = 12,
}) => {
  const [hover, toggleHover] = useState(false);
  const [focus, toggleFocus] = useState(false);
  const locale = useLocale();
  const [fieldLocale, toggleFieldLocale] = useState(locale);

  // actualName is used to pass locale info to LocaleString typed fields
  const fieldName = translations ? [name, fieldLocale].join(".") : name;

  // other states
  const { control, setValue, trigger } = useFormContext();
  const { field } = useController({ name: fieldName, control });
  const { errors } = useFormState();

  const onChange = (e: any) => {
    // don't change, if disabled
    if (disabled) {
      return;
    }

    // set the value to state (not sure if needed?)
    setValue(fieldName, e.target.value);

    // delegate
    field.onChange(e);

    // trigger validation
    trigger(fieldName);
  };

  // get error
  const error = translations
    ? getTranslationError(errors, fieldName)
    : getError(errors, fieldName);
  return (
    <Controller
      key={fieldName}
      name={fieldName}
      control={control}
      render={({ field: { ref, ...fieldProps } }) => (
        <Field>
          <Label name={fieldName} required={required}>
            {label}
          </Label>
          <StyledInputContainer>
            <StyledTextarea
              $height={height}
              key={fieldName}
              id={`id_${fieldName}`}
              disabled={disabled}
              maxLength={maxLength}
              ref={ref}
              value={fieldProps.value}
              onChange={onChange}
              name={fieldProps.name}
              // some aria flags
              aria-required={required}
              aria-label={label}
              aria-invalid={error !== undefined}
              autoComplete={autoComplete}
              onMouseEnter={() => toggleHover(true)}
              onMouseLeave={() => toggleHover(false)}
              onFocus={() => toggleFocus(true)}
              onBlur={() => {
                toggleFocus(false);
              }}
              $hover={hover}
              $focus={focus}
            />
            {translations && (
              <TranslationSelector
                locale={fieldLocale}
                label={label}
                name={name}
                type="textarea"
                onChange={toggleFieldLocale}
              />
            )}
          </StyledInputContainer>
          <Warning name={fieldName} translations={translations} />
        </Field>
      )}
    />
  );
};

export default TextareaInput;
